/**
 * AdminOnboardingSlidesData.jsx
 * ==============================
 *
 * 15 slides natives JSX pour l'onboarding ADMINISTRATEUR de Baobab Connect.
 * Même philosophie que le guide client (palette crème/emerald/gold, 16:9),
 * avec un eyebrow "Espace Administrateur" pour la différenciation.
 *
 * Réutilise les helpers exportés de OnboardingSlidesData.jsx (DRY).
 */
import React from 'react';
import {
  Shield, Gauge, Users,
  CheckCircle2, FileText, Calendar, Truck, FileCheck2, Receipt, Banknote,
  KeyRound, Smartphone, LockKeyhole,
  LineChart, TrendingUp, AlertTriangle,
  ClipboardCheck, ShieldAlert, FileSearch,
  Activity, ListChecks, MapPin,
  PackageSearch, UserCog, MapPinned,
  Building2, Building, ContactRound,
  Handshake, Timer, Flame,
  MessageSquare, Link as LinkIcon, Inbox,
  Lock, Network, Eye,
  UserCheck, ShieldCheck, Crown,
  BarChart3, Download, Mail,
  PhoneCall, Database, Sparkles, Quote,
} from 'lucide-react';

import {
  SlideShell, SlideHeader, PillarCard, StepBubble, Footnote, ScreenshotFrame,
  ADMIN_ASSET,
} from './OnboardingSlidesData';

// ===================================================================
// SLIDE 1 — Couverture Admin
// ===================================================================
const Slide1 = () => (
  <SlideShell testId="admin-slide-1" dark className="overflow-hidden">
    <div
      className="absolute inset-0 opacity-[0.05]"
      style={{
        backgroundImage:
          'radial-gradient(circle at 25% 30%, #FCD34D 0%, transparent 50%), radial-gradient(circle at 80% 70%, #A7F3D0 0%, transparent 45%)',
      }}
    />
    <div className="relative z-10 flex-1 grid md:grid-cols-[1fr_auto] gap-12 items-center px-12 md:px-24">
      <div>
        <div className="inline-flex items-center gap-3 px-4 py-1.5 rounded-full bg-[#FCD34D]/15 border border-[#FCD34D]/30 mb-6">
          <Shield className="w-4 h-4 text-[#FCD34D]" />
          <span className="text-xs font-bold tracking-[0.25em] uppercase text-[#FCD34D]">
            Espace Administrateur · Staff interne
          </span>
        </div>
        <h1
          className="text-4xl md:text-6xl lg:text-7xl font-extrabold leading-[1.05] tracking-tight text-white max-w-4xl"
          style={{ fontFamily: 'Georgia, "Playfair Display", serif' }}
        >
          Pilotez Baobab Connect <span className="text-[#FCD34D]">en maître.</span>
        </h1>
        <p className="mt-8 text-xl md:text-2xl text-[#A7F3D0] max-w-3xl leading-relaxed">
          Formation d'onboarding pour les administrateurs et le staff opérationnel.
        </p>
        <div className="mt-12 flex items-center gap-6 text-[#A7F3D0]/80 text-sm">
          <span className="font-mono">v3.0 · Mai 2026</span>
          <span>·</span>
          <span>≈ 10 min de lecture</span>
          <span>·</span>
          <span>15 slides</span>
        </div>
      </div>
      <div className="hidden md:flex flex-col items-center justify-center">
        <div className="w-64 h-64 lg:w-80 lg:h-80 rounded-3xl bg-white/95 p-6 shadow-2xl flex items-center justify-center">
          <img
            src={ADMIN_ASSET('baobab_logo.png')}
            alt="Baobab Connect"
            className="w-full h-full object-contain"
          />
        </div>
      </div>
    </div>
    <div className="absolute bottom-6 left-12 md:left-24 right-12 md:right-24 flex justify-between items-center text-xs text-[#A7F3D0]/70">
      <div className="font-semibold tracking-wide uppercase">Baobab Connect · Onboarding Admin</div>
      <div className="tabular-nums">© 2026</div>
    </div>
  </SlideShell>
);

// ===================================================================
// SLIDE 2 — Le rôle de l'administrateur
// ===================================================================
const Slide2 = () => (
  <SlideShell testId="admin-slide-2">
    <SlideHeader
      eyebrow="Votre mission"
      title="Trois leviers, un seul cockpit."
      subtitle="L'administrateur orchestre l'activité de la plateforme : santé financière, qualité opérationnelle et excellence client."
    />
    <div className="flex-1 flex items-center px-12 md:px-20 pb-20">
      <div className="grid md:grid-cols-3 gap-6 w-full">
        <PillarCard
          Icon={Gauge}
          accent="#047857"
          badge="Pilotage"
          title="Vue exécutive en temps réel"
          body="KPIs CFO, marge brute, DSO, concentration client. Vous décidez en quelques secondes, pas en jours."
        />
        <PillarCard
          Icon={ShieldCheck}
          accent="#D97706"
          badge="Gouvernance"
          title="160 permissions, 14 rôles"
          body="Le RBAC V2 garantit la séparation des pouvoirs : finance, ops, commercial, audit. Traçabilité totale."
        />
        <PillarCard
          Icon={Users}
          accent="#0EA5E9"
          badge="Opérations"
          title="Clients, chauffeurs, flotte"
          body="Un seul écran pour gérer l'écosystème : 26 clients, plusieurs dizaines de chauffeurs, des dizaines de véhicules."
        />
      </div>
    </div>
    <Footnote />
  </SlideShell>
);

// ===================================================================
// SLIDE 3 — Cycle de vie côté admin
// ===================================================================
const Slide3 = () => {
  const steps = [
    { Icon: FileText, num: 1, title: 'Brouillon', sub: 'Demande reçue' },
    { Icon: Handshake, num: 2, title: 'Devis émis', sub: 'Tarif + créneau' },
    { Icon: Calendar, num: 3, title: 'Planifiée', sub: 'Chauffeur OK' },
    { Icon: Truck, num: 4, title: 'En cours', sub: 'GPS actif' },
    { Icon: FileCheck2, num: 5, title: 'Livrée', sub: 'BD signé' },
    { Icon: Receipt, num: 6, title: 'Facturée', sub: 'PDF émis' },
    { Icon: Banknote, num: 7, title: 'Payée', sub: 'Encaissé' },
  ];
  return (
    <SlideShell testId="admin-slide-3">
      <SlideHeader
        eyebrow="Vue d'ensemble"
        title="Le cycle complet, vu de l'administrateur."
        subtitle="Sept transitions, chacune génère un événement loggé. Vous savez qui a fait quoi, quand, et pourquoi."
      />
      <div className="flex-1 flex items-center px-6 md:px-12 pb-20">
        <div className="w-full">
          <div className="flex items-start justify-between gap-1 md:gap-2 relative">
            <div className="absolute top-10 md:top-12 left-[7%] right-[7%] border-t-2 border-dashed border-[#047857]/40 -z-0" />
            {steps.map((s) => (
              <StepBubble key={s.num} {...s} />
            ))}
          </div>
        </div>
      </div>
      <Footnote />
    </SlideShell>
  );
};

// ===================================================================
// SLIDE 4 — Sécurité renforcée (2FA, mots de passe, audit)
// ===================================================================
const Slide4 = () => (
  <SlideShell testId="admin-slide-4">
    <SlideHeader
      eyebrow="Étape 1 / 11"
      title="Sécurité de votre compte admin."
      subtitle="Vous avez des super-pouvoirs. La plateforme exige donc une hygiène stricte de sécurité."
    />
    <div className="flex-1 grid md:grid-cols-3 gap-6 px-12 md:px-20 pb-20 items-stretch">
      <PillarCard
        Icon={KeyRound}
        accent="#047857"
        badge="Mot de passe fort"
        title="16 caractères minimum"
        body="Majuscule, chiffre, caractère spécial. Renouvelé tous les 90 jours. Bcrypt avec 12 rounds en BDD."
      />
      <PillarCard
        Icon={Smartphone}
        accent="#D97706"
        badge="2FA obligatoire"
        title="Code TOTP (Google Authenticator)"
        body="Tous les admins et le staff financier doivent activer la double authentification. Aucune exception."
      />
      <PillarCard
        Icon={Eye}
        accent="#0EA5E9"
        badge="Audit trail"
        title="Chaque action est tracée"
        body="Logs d'audit immuables : qui a modifié quoi, IP source, navigateur. Conservés 5 ans (OHADA)."
      />
    </div>
    <Footnote />
  </SlideShell>
);

// ===================================================================
// SLIDE 5 — Cockpit CFO (vue exécutive)
// ===================================================================
const Slide5 = () => (
  <SlideShell testId="admin-slide-5">
    <SlideHeader
      eyebrow="Étape 2 / 11"
      title="Cockpit CFO — vue exécutive."
      subtitle="L'écran qui dit la vérité financière de l'entreprise en 5 secondes."
    />
    <div className="flex-1 grid md:grid-cols-[1.4fr_1fr] gap-8 px-12 md:px-20 pb-20 items-center">
      <ScreenshotFrame src={ADMIN_ASSET('img_009.png')} alt="Cockpit CFO" caption="Le Cockpit CFO — 12 mois glissants, alertes et insights" />
      <div className="space-y-4">
        <div className="bg-white border-l-4 border-[#047857] rounded-r-lg p-4">
          <div className="text-xs font-bold tracking-wider uppercase text-[#047857] mb-1">CA & Marge</div>
          <div className="font-bold text-[#0F172A] mb-1">12 mois glissants</div>
          <div className="text-sm text-[#475569]">CA total, marge brute %, MoM, YoY. Calcul réel en BDD.</div>
        </div>
        <div className="bg-white border-l-4 border-[#D97706] rounded-r-lg p-4">
          <div className="text-xs font-bold tracking-wider uppercase text-[#D97706] mb-1">Risque · DSO</div>
          <div className="font-bold text-[#0F172A] mb-1">Encours, DSO, concentration</div>
          <div className="text-sm text-[#475569]">Alertes auto si DSO &gt; 45j ou top-client &gt; 50% du CA.</div>
        </div>
        <div className="bg-white border-l-4 border-[#0EA5E9] rounded-r-lg p-4">
          <div className="text-xs font-bold tracking-wider uppercase text-[#0EA5E9] mb-1">Insights du jour</div>
          <div className="font-bold text-[#0F172A] mb-1">Décisions à prendre</div>
          <div className="text-sm text-[#475569]">Trésorerie attendue, ops sans facture, top-client.</div>
        </div>
      </div>
    </div>
    <Footnote />
  </SlideShell>
);

// ===================================================================
// SLIDE 6 — Audit Factures (qualité comptable)
// ===================================================================
const Slide6 = () => (
  <SlideShell testId="admin-slide-6">
    <SlideHeader
      eyebrow="Étape 3 / 11"
      title="Audit Factures — qualité comptable."
      subtitle="Le moteur qui détecte les doublons, les incohérences et les factures orphelines. Conformité OHADA garantie."
    />
    <div className="flex-1 grid md:grid-cols-[1.2fr_1fr] gap-8 px-12 md:px-20 pb-20 items-center">
      <ScreenshotFrame src={ADMIN_ASSET('img_008.png')} alt="Audit Factures" caption="Score de qualité comptable, doublons et anomalies détectés en temps réel" />
      <div className="space-y-4">
        <div className="bg-white border-l-4 border-[#047857] rounded-r-lg p-4">
          <div className="text-xs font-bold tracking-wider uppercase text-[#047857] mb-1">Doublons</div>
          <div className="font-bold text-[#0F172A] mb-1">Numéros & périodes</div>
          <div className="text-sm text-[#475569]">Détection croisée : numéro spot, contrat + période.</div>
        </div>
        <div className="bg-white border-l-4 border-[#D97706] rounded-r-lg p-4">
          <div className="text-xs font-bold tracking-wider uppercase text-[#D97706] mb-1">Factures contrat sans période</div>
          <div className="font-bold text-[#0F172A] mb-1">Données manquantes</div>
          <div className="text-sm text-[#475569]">Signalées en rouge. À régulariser sous 48 h.</div>
        </div>
        <div className="bg-white border-l-4 border-[#0EA5E9] rounded-r-lg p-4">
          <div className="text-xs font-bold tracking-wider uppercase text-[#0EA5E9] mb-1">Runs orphelins</div>
          <div className="font-bold text-[#0F172A] mb-1">Ops sans facture</div>
          <div className="text-sm text-[#475569]">Opérations livrées mais non facturées = manque à gagner.</div>
        </div>
      </div>
    </div>
    <Footnote />
  </SlideShell>
);

// ===================================================================
// SLIDE 7 — Gestion des Opérations
// ===================================================================
const Slide7 = () => (
  <SlideShell testId="admin-slide-7">
    <SlideHeader
      eyebrow="Étape 4 / 11"
      title="Gestion des opérations."
      subtitle="Le centre névralgique : 100 opérations vivantes, 4 phases visibles d'un coup d'œil."
    />
    <div className="flex-1 grid md:grid-cols-[1.4fr_1fr] gap-8 px-12 md:px-20 pb-20 items-center">
      <ScreenshotFrame src={ADMIN_ASSET('img_005.png')} alt="Gestion des Opérations" caption="Toutes les ops actives, classées par phase et triables par statut financier" />
      <div className="space-y-3">
        {[
          ['#047857', 'Préparation', 'Devis émis, à planifier'],
          ['#0EA5E9', 'Exécution', 'En transit, GPS actif'],
          ['#D97706', 'Facturation', 'Livrées, à facturer ou en attente paiement'],
          ['#10B981', 'Terminées', 'Payées et clôturées'],
        ].map(([color, t, d]) => (
          <div key={t} className="bg-white border-l-4 rounded-r-lg p-3" style={{ borderColor: color }}>
            <div className="text-xs font-bold tracking-wider uppercase mb-1" style={{ color }}>
              {t}
            </div>
            <div className="text-sm text-[#475569]">{d}</div>
          </div>
        ))}
      </div>
    </div>
    <Footnote />
  </SlideShell>
);

// ===================================================================
// SLIDE 8 — Détail opération & assignation
// ===================================================================
const Slide8 = () => (
  <SlideShell testId="admin-slide-8">
    <SlideHeader
      eyebrow="Étape 5 / 11"
      title="Détail opération & assignations."
      subtitle="Le poste de pilotage d'une expédition : itinéraire, chauffeurs, véhicules, responsables."
    />
    <div className="flex-1 grid md:grid-cols-[1.4fr_1fr] gap-8 px-12 md:px-20 pb-20 items-center">
      <ScreenshotFrame src={ADMIN_ASSET('img_006.png')} alt="Détail d'une opération" caption="Carte GPS, chauffeurs assignés, prix estimé — tout en un seul écran" />
      <div className="space-y-3">
        <div className="bg-white border-l-4 border-[#047857] rounded-r-lg p-3">
          <div className="text-xs font-bold tracking-wider uppercase text-[#047857] mb-1">Responsables</div>
          <div className="text-sm text-[#475569]">Opérateur Baobab + Field Ops. Réassignables à tout moment.</div>
        </div>
        <div className="bg-white border-l-4 border-[#D97706] rounded-r-lg p-3">
          <div className="text-xs font-bold tracking-wider uppercase text-[#D97706] mb-1">Camions</div>
          <div className="text-sm text-[#475569]">Multi-camions possible. Chacun lié à un chauffeur + une plaque.</div>
        </div>
        <div className="bg-white border-l-4 border-[#0EA5E9] rounded-r-lg p-3">
          <div className="text-xs font-bold tracking-wider uppercase text-[#0EA5E9] mb-1">Itinéraire</div>
          <div className="text-sm text-[#475569]">Carte interactive, distance, type de trajet, tonnage.</div>
        </div>
      </div>
    </div>
    <Footnote />
  </SlideShell>
);

// ===================================================================
// SLIDE 9 — Gestion des Clients
// ===================================================================
const Slide9 = () => (
  <SlideShell testId="admin-slide-9">
    <SlideHeader
      eyebrow="Étape 6 / 11"
      title="Gestion des clients."
      subtitle="Votre carnet d'adresses B2B, segmenté et toujours à jour."
    />
    <div className="flex-1 grid md:grid-cols-[1.4fr_1fr] gap-8 px-12 md:px-20 pb-20 items-center">
      <ScreenshotFrame src={ADMIN_ASSET('img_003.png')} alt="Gestion des Clients" caption="Vue globale : KPIs, filtres par secteur, import CSV en un clic" />
      <div className="space-y-3">
        <div className="bg-white border-l-4 border-[#047857] rounded-r-lg p-3">
          <div className="text-xs font-bold tracking-wider uppercase text-[#047857] mb-1">Segmentation</div>
          <div className="text-sm text-[#475569]">Particuliers vs Entreprises · Actifs vs Inactifs · Par secteur.</div>
        </div>
        <div className="bg-white border-l-4 border-[#D97706] rounded-r-lg p-3">
          <div className="text-xs font-bold tracking-wider uppercase text-[#D97706] mb-1">Politique de paiement</div>
          <div className="text-sm text-[#475569]">Configurable par client : Default, 30j, 60j, Cash, escompte.</div>
        </div>
        <div className="bg-white border-l-4 border-[#0EA5E9] rounded-r-lg p-3">
          <div className="text-xs font-bold tracking-wider uppercase text-[#0EA5E9] mb-1">Import CSV</div>
          <div className="text-sm text-[#475569]">Migration de votre base existante en quelques minutes.</div>
        </div>
      </div>
    </div>
    <Footnote />
  </SlideShell>
);

// ===================================================================
// SLIDE 10 — Centre de Négociations & SLA
// ===================================================================
const Slide10 = () => (
  <SlideShell testId="admin-slide-10">
    <SlideHeader
      eyebrow="Étape 7 / 11"
      title="Centre de négociations & SLA."
      subtitle="Toutes les demandes de révision client, avec un SLA temps réel pour ne jamais rater de deal."
    />
    <div className="flex-1 grid md:grid-cols-[1.3fr_1fr] gap-8 px-12 md:px-20 pb-20 items-center">
      <ScreenshotFrame src={ADMIN_ASSET('img_007.png')} alt="Centre de Négociations" caption="KPIs temps réel : en cours, retard SLA, taux d'acceptation, volume négocié" />
      <div className="space-y-3">
        <div className="bg-white border-l-4 border-[#047857] rounded-r-lg p-3">
          <div className="text-xs font-bold tracking-wider uppercase text-[#047857] mb-1">SLA actif</div>
          <div className="text-sm text-[#475569]">Décompte minute par minute. Alertes auto à 80% du délai.</div>
        </div>
        <div className="bg-white border-l-4 border-[#D97706] rounded-r-lg p-3">
          <div className="text-xs font-bold tracking-wider uppercase text-[#D97706] mb-1">Escalades</div>
          <div className="text-sm text-[#475569]">Notifications vers le lead commercial si dossier bloqué.</div>
        </div>
        <div className="bg-white border-l-4 border-[#0EA5E9] rounded-r-lg p-3">
          <div className="text-xs font-bold tracking-wider uppercase text-[#0EA5E9] mb-1">Décisions inline</div>
          <div className="text-sm text-[#475569]">Accepter / refuser / contre-proposer sans changer d'écran.</div>
        </div>
      </div>
    </div>
    <Footnote />
  </SlideShell>
);

// ===================================================================
// SLIDE 11 — Messagerie centralisée
// ===================================================================
const Slide11 = () => (
  <SlideShell testId="admin-slide-11">
    <SlideHeader
      eyebrow="Étape 8 / 11"
      title="Messagerie centralisée."
      subtitle="Un thread par opération, un par client. Plus jamais de message perdu, plus jamais d'historique éclaté."
    />
    <div className="flex-1 grid md:grid-cols-[1.3fr_1fr] gap-8 px-12 md:px-20 pb-20 items-center">
      <ScreenshotFrame src={ADMIN_ASSET('img_012.png')} alt="Messagerie" caption="Toutes les conversations, filtrables par opération, statut ou non-lues" />
      <div className="space-y-3">
        <div className="bg-white border-l-4 border-[#047857] rounded-r-lg p-3">
          <div className="text-xs font-bold tracking-wider uppercase text-[#047857] mb-1">Contexte opération</div>
          <div className="text-sm text-[#475569]">Chaque thread est lié à une op. Le client et l'admin voient le même contexte.</div>
        </div>
        <div className="bg-white border-l-4 border-[#D97706] rounded-r-lg p-3">
          <div className="text-xs font-bold tracking-wider uppercase text-[#D97706] mb-1">Non-lues prioritaires</div>
          <div className="text-sm text-[#475569]">Compteur en temps réel + filtre dédié. Aucun client ignoré.</div>
        </div>
        <div className="bg-white border-l-4 border-[#0EA5E9] rounded-r-lg p-3">
          <div className="text-xs font-bold tracking-wider uppercase text-[#0EA5E9] mb-1">Pièces jointes</div>
          <div className="text-sm text-[#475569]">Photos terrain, PDF, BD signés. Stockage objet sécurisé.</div>
        </div>
      </div>
    </div>
    <Footnote />
  </SlideShell>
);

// ===================================================================
// SLIDE 12 — Audit RBAC V2
// ===================================================================
const Slide12 = () => (
  <SlideShell testId="admin-slide-12">
    <SlideHeader
      eyebrow="Étape 9 / 11"
      title="Audit RBAC V2."
      subtitle="160 permissions cataloguées, 14 rôles actifs, 17 modules métier. Le moteur de gouvernance de la plateforme."
    />
    <div className="flex-1 grid md:grid-cols-[1.4fr_1fr] gap-8 px-12 md:px-20 pb-20 items-center">
      <ScreenshotFrame src={ADMIN_ASSET('img_004.png')} alt="Audit RBAC V2" caption="Matrice rôles × permissions, simulateur « voir comme [rôle] »" />
      <div className="space-y-3">
        <div className="bg-gradient-to-br from-[#FCD34D]/15 to-[#D97706]/10 border border-[#D97706]/30 rounded-lg p-4">
          <div className="text-xs font-bold tracking-wider uppercase text-[#D97706] mb-1">Lecture seule</div>
          <div className="text-sm text-[#475569]">L'audit ne modifie rien. Pour éditer, utilisez Paramètres → Rôles.</div>
        </div>
        <div className="bg-white border-l-4 border-[#047857] rounded-r-lg p-3">
          <div className="text-xs font-bold tracking-wider uppercase text-[#047857] mb-1">Matrice</div>
          <div className="text-sm text-[#475569]">Visualise instantanément ce que chaque rôle peut faire.</div>
        </div>
        <div className="bg-white border-l-4 border-[#0EA5E9] rounded-r-lg p-3">
          <div className="text-xs font-bold tracking-wider uppercase text-[#0EA5E9] mb-1">« Voir comme »</div>
          <div className="text-sm text-[#475569]">Impersonate un rôle pour tester l'expérience d'accès.</div>
        </div>
      </div>
    </div>
    <Footnote />
  </SlideShell>
);

// ===================================================================
// SLIDE 13 — Gestion des Rôles (équipes internes)
// ===================================================================
const Slide13 = () => (
  <SlideShell testId="admin-slide-13">
    <SlideHeader
      eyebrow="Étape 10 / 11"
      title="Gestion des rôles & équipes."
      subtitle="Définissez qui peut faire quoi. La hiérarchie est explicite, la délégation contrôlée."
    />
    <div className="flex-1 grid md:grid-cols-[1.3fr_1fr] gap-8 px-12 md:px-20 pb-20 items-center">
      <ScreenshotFrame src={ADMIN_ASSET('img_011.png')} alt="Gestion des Rôles" caption="Vue synthétique : Administrateurs · Commerciaux · Opérateurs · Auditeurs" />
      <div className="space-y-3">
        <div className="bg-white border-l-4 border-[#7C3AED] rounded-r-lg p-3">
          <div className="flex items-center gap-2 mb-1">
            <Crown className="w-4 h-4 text-[#7C3AED]" />
            <div className="text-xs font-bold tracking-wider uppercase text-[#7C3AED]">Administrateur</div>
          </div>
          <div className="text-sm text-[#475569]">Accès complet. 3 personnes max recommandé.</div>
        </div>
        <div className="bg-white border-l-4 border-[#047857] rounded-r-lg p-3">
          <div className="flex items-center gap-2 mb-1">
            <UserCheck className="w-4 h-4 text-[#047857]" />
            <div className="text-xs font-bold tracking-wider uppercase text-[#047857]">Commercial</div>
          </div>
          <div className="text-sm text-[#475569]">Devis, clients, négociations. Pas d'accès RBAC.</div>
        </div>
        <div className="bg-white border-l-4 border-[#0EA5E9] rounded-r-lg p-3">
          <div className="flex items-center gap-2 mb-1">
            <Truck className="w-4 h-4 text-[#0EA5E9]" />
            <div className="text-xs font-bold tracking-wider uppercase text-[#0EA5E9]">Opérateur</div>
          </div>
          <div className="text-sm text-[#475569]">Planification, suivi, bons signés. Pas de finances.</div>
        </div>
      </div>
    </div>
    <Footnote />
  </SlideShell>
);

// ===================================================================
// SLIDE 14 — Centre de Rapports
// ===================================================================
const Slide14 = () => (
  <SlideShell testId="admin-slide-14">
    <SlideHeader
      eyebrow="Étape 11 / 11"
      title="Centre de rapports."
      subtitle="Générez et diffusez vos rapports de performance — mensuels, sur mesure, ou planifiés."
    />
    <div className="flex-1 grid md:grid-cols-[1.4fr_1fr] gap-8 px-12 md:px-20 pb-20 items-center">
      <ScreenshotFrame src={ADMIN_ASSET('img_013.png')} alt="Centre de Rapports" caption="Rapport mensuel complet · Performance · Spot · KPIs visuels" />
      <div className="space-y-3">
        <div className="bg-white border-l-4 border-[#047857] rounded-r-lg p-3">
          <div className="text-xs font-bold tracking-wider uppercase text-[#047857] mb-1">Mensuel complet</div>
          <div className="text-sm text-[#475569]">PDF A4, 6 sections : exécutif, activité, perf, finances, contrats, flotte.</div>
        </div>
        <div className="bg-white border-l-4 border-[#D97706] rounded-r-lg p-3">
          <div className="text-xs font-bold tracking-wider uppercase text-[#D97706] mb-1">Performance</div>
          <div className="text-sm text-[#475569]">PDF paysage avec KPIs visuels, graphiques et tableaux détaillés.</div>
        </div>
        <div className="bg-white border-l-4 border-[#0EA5E9] rounded-r-lg p-3">
          <div className="flex items-center gap-2 mb-1">
            <Mail className="w-4 h-4 text-[#0EA5E9]" />
            <div className="text-xs font-bold tracking-wider uppercase text-[#0EA5E9]">Envoi automatique</div>
          </div>
          <div className="text-sm text-[#475569]">Planification mensuelle, destinataires multiples. À configurer.</div>
        </div>
      </div>
    </div>
    <Footnote />
  </SlideShell>
);

// ===================================================================
// SLIDE 15 — Golden Rules Admin + Ressources
// ===================================================================
const Slide15 = () => (
  <SlideShell testId="admin-slide-15" dark className="overflow-hidden">
    <div className="absolute inset-0 opacity-[0.05]" style={{ backgroundImage: 'radial-gradient(circle at 70% 30%, #FCD34D 0%, transparent 50%)' }} />
    <div className="relative z-10 flex-1 flex flex-col justify-center px-12 md:px-24">
      <Quote className="w-12 h-12 text-[#FCD34D] mb-6" strokeWidth={1.5} />
      <blockquote
        className="text-3xl md:text-5xl lg:text-6xl font-extrabold leading-[1.1] text-white max-w-5xl"
        style={{ fontFamily: 'Georgia, "Playfair Display", serif' }}
      >
        Un admin discipliné, c'est <span className="text-[#FCD34D]">une plateforme fiable</span>.
      </blockquote>
      <div className="mt-10 grid md:grid-cols-3 gap-6 max-w-5xl">
        <div className="bg-white/10 border border-white/20 rounded-xl p-5">
          <ShieldAlert className="w-6 h-6 text-[#FCD34D] mb-2" />
          <div className="text-sm font-bold text-white mb-1">2FA non négociable</div>
          <div className="text-xs text-[#A7F3D0] leading-relaxed">Activez-le dès la première connexion.</div>
        </div>
        <div className="bg-white/10 border border-white/20 rounded-xl p-5">
          <ClipboardCheck className="w-6 h-6 text-[#FCD34D] mb-2" />
          <div className="text-sm font-bold text-white mb-1">Audit hebdo</div>
          <div className="text-xs text-[#A7F3D0] leading-relaxed">Score qualité Factures + DSO chaque lundi.</div>
        </div>
        <div className="bg-white/10 border border-white/20 rounded-xl p-5">
          <Database className="w-6 h-6 text-[#FCD34D] mb-2" />
          <div className="text-sm font-bold text-white mb-1">Sauvegardes</div>
          <div className="text-xs text-[#A7F3D0] leading-relaxed">Vérifiez les exports mensuels CSV/PDF.</div>
        </div>
      </div>
      <div className="mt-12 grid md:grid-cols-3 gap-6 max-w-5xl">
        <div className="text-[#A7F3D0]">
          <div className="text-xs uppercase tracking-wider font-bold text-[#FCD34D] mb-1">Support interne</div>
          <div className="font-mono text-sm">tech@baobabco.net</div>
        </div>
        <div className="text-[#A7F3D0]">
          <div className="text-xs uppercase tracking-wider font-bold text-[#FCD34D] mb-1">Guide complet</div>
          <div className="text-sm">/docs/admin</div>
        </div>
        <div className="text-[#A7F3D0]">
          <div className="text-xs uppercase tracking-wider font-bold text-[#FCD34D] mb-1">Astreinte</div>
          <div className="text-sm">24/7 via Slack #ops-alerts</div>
        </div>
      </div>
    </div>
    <div className="absolute bottom-6 left-12 md:left-24 right-12 md:right-24 flex justify-between items-center text-xs text-[#A7F3D0]/70">
      <div className="font-semibold tracking-wide uppercase">Baobab Connect · Onboarding Admin</div>
      <div className="tabular-nums">© 2026</div>
    </div>
  </SlideShell>
);

// -------------------------------------------------------------------
// Export ordonné
// -------------------------------------------------------------------
export const ADMIN_ONBOARDING_SLIDES = [
  { id: 1, title: 'Bienvenue Admin', Component: Slide1 },
  { id: 2, title: 'Trois leviers', Component: Slide2 },
  { id: 3, title: 'Cycle de vie complet', Component: Slide3 },
  { id: 4, title: 'Sécurité du compte', Component: Slide4 },
  { id: 5, title: 'Cockpit CFO', Component: Slide5 },
  { id: 6, title: 'Audit Factures', Component: Slide6 },
  { id: 7, title: 'Gestion des Opérations', Component: Slide7 },
  { id: 8, title: 'Détail opération', Component: Slide8 },
  { id: 9, title: 'Gestion des Clients', Component: Slide9 },
  { id: 10, title: 'Négociations & SLA', Component: Slide10 },
  { id: 11, title: 'Messagerie', Component: Slide11 },
  { id: 12, title: 'Audit RBAC', Component: Slide12 },
  { id: 13, title: 'Gestion des Rôles', Component: Slide13 },
  { id: 14, title: 'Centre de Rapports', Component: Slide14 },
  { id: 15, title: 'Golden Rules Admin', Component: Slide15 },
];

export default ADMIN_ONBOARDING_SLIDES;
